import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import LegacyFunnel from "./components/LegacyFunnel";
import LegacyShortForm from "./components/LegacyShortForm";

const defaults = {
  brand_name: "Legacy Ease",
  logo_url: "",
  favicon_url: "/assets/legacy-ease-icon.svg",
  hero_image_url: "/assets/legacy-ease-family-hero.webp",
  supporting_image_url: "/assets/legacy-ease-family-hero.webp",
  phone: "",
};

export default function Home({ startFunnel = false }) {
  const campaignParams = new URLSearchParams(window.location.search);
  const campaignKey = campaignParams.get("lp_campaign") || "";
  const isShortCampaign = campaignParams.get("landing_variant") === "portal_landing_page" && /^lpc_[a-f0-9]{64}$/i.test(campaignKey);
  const [settings, setSettings] = useState(defaults);
  const [showFunnel, setShowFunnel] = useState(startFunnel || isShortCampaign);
  const funnelRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetch("/api/site_settings.php", { headers: { Accept: "application/json" } })
      .then((r) => r.json()).then((d) => d.success && setSettings({ ...defaults, ...d.settings })).catch(() => {});
  }, []);

  useEffect(() => {
    if (!settings.favicon_url) return;
    let icon = document.querySelector("link[rel~='icon']");
    if (!icon) { icon = document.createElement("link"); icon.rel = "icon"; document.head.appendChild(icon); }
    icon.href = settings.favicon_url;
  }, [settings.favicon_url]);

  const begin = () => {
    setShowFunnel(true);
    const sessionId = window.__legacyAttribution?.getIds?.().session_id;
    navigate(`/apply${window.location.search}${sessionId ? `#le_session=${encodeURIComponent(sessionId)}` : ""}`, { replace: false });
    window.legacyEvents?.track("funnel_started", { funnel_variant: isShortCampaign ? "short_form" : "default" });
    setTimeout(() => funnelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  return (
    <div className="site-shell">
      <header className="site-header">
        <a className="brand" href="/" aria-label="Legacy Ease home">
          {settings.logo_url ? <img src={settings.logo_url} alt={settings.brand_name} /> : <><span className="brand-mark">LE</span><span>{settings.brand_name}</span></>}
        </a>
        <div className="header-trust"><span className="trust-dot" /> Secure, private request</div>
      </header>

      <main>
        <section className="hero" style={{ "--hero-image": `url("${settings.hero_image_url}")` }}>
          <div className="hero-overlay" />
          <div className="hero-copy">
            <div className="eyebrow">Simple final expense protection</div>
            <h1>Leave love behind.<br/><em>Not a burden.</em></h1>
            <p className="hero-lede">Explore affordable coverage designed to help your family handle funeral costs, medical bills, and final wishes—with a simple, no-pressure process.</p>
            <button className="primary-cta" onClick={begin}>See My Options <span>→</span></button>
            <div className="hero-notes"><span>✓ No obligation</span><span>✓ Quick qualification</span><span>✓ Licensed help</span></div>
          </div>
        </section>

        <section className="proof-strip" aria-label="Coverage benefits">
          <div><strong>$5K–$35K</strong><span>Coverage options</span></div>
          <div><strong>Simple</strong><span>Health questions</span></div>
          <div><strong>Personal</strong><span>Licensed guidance</span></div>
          <div><strong>Protected</strong><span>Private information</span></div>
        </section>

        {showFunnel && <section className="funnel-section" ref={funnelRef}>{isShortCampaign ? <LegacyShortForm /> : <LegacyFunnel />}</section>}

        <section className="why-section">
          <div className="section-heading"><span>Plan with confidence</span><h2>A small plan can make a meaningful difference.</h2><p>Final expense coverage can give the people you love room to grieve—not a stack of unexpected bills.</p></div>
          <div className="benefit-grid">
            <article><b>01</b><h3>Help protect your family</h3><p>Give loved ones funds they may use for funeral costs, final bills, or other needs.</p></article>
            <article><b>02</b><h3>Choose what fits</h3><p>Review coverage levels designed around your priorities and monthly budget.</p></article>
            <article><b>03</b><h3>Get human guidance</h3><p>A licensed insurance professional can explain your options without pressure.</p></article>
          </div>
        </section>

        <section className="story-section">
          <div className="story-image" style={{ backgroundImage: `url("${settings.supporting_image_url}")` }} />
          <div className="story-copy"><span className="eyebrow dark">Your wishes. Your legacy.</span><h2>Make tomorrow a little easier for the people who matter most.</h2><p>Planning ahead is an act of care. Legacy Ease helps you take the first step in minutes, from the privacy of home.</p><button className="secondary-cta" onClick={begin}>Start My Free Request</button></div>
        </section>

        <section className="faq-section"><div><span className="eyebrow dark">Straight answers</span><h2>What to expect</h2></div><div className="faq-list">
          <details open><summary>Is this a quote or an application?</summary><p>This is a request to review options. A licensed insurance professional will confirm availability, eligibility, rates, and policy details with you.</p></details>
          <details><summary>Will I need a medical exam?</summary><p>Many final expense plans use health questions instead of an exam. Product and eligibility rules vary.</p></details>
          <details><summary>How is my information used?</summary><p>Your information is used to respond to your coverage request. Health answers remain in our private lead system and are not sent to advertising platforms.</p></details>
        </div></section>

        <section className="final-cta"><span>One thoughtful step today</span><h2>Help protect the story you leave behind.</h2><button className="primary-cta light" onClick={begin}>See My Options <span>→</span></button></section>
      </main>

      <footer><div className="footer-brand">{settings.brand_name}</div><p>Legacy Ease connects consumers with licensed insurance professionals. Not affiliated with or endorsed by the U.S. government or federal Medicare program. Product availability and eligibility vary by state and carrier.</p><div><Link to="/privacy">Privacy</Link><Link to="/terms">Terms</Link><Link to="/login">Admin</Link></div><small>© {new Date().getFullYear()} Legacy Ease. All rights reserved.<span style={{ marginLeft: ".65rem" }}>Operated by <a href="https://lionpridefinancial.com" style={{ marginRight: 0, color: "inherit", textDecoration: "underline", textUnderlineOffset: "2px" }}>Lion Pride Financial</a>.</span></small></footer>
    </div>
  );
}
